import { useEffect, useState } from "react";
import type { WordDTO } from "@shared/types";
import { speak } from "../audio";
import { GameImage } from "./GameImage";

interface Props {
  words: WordDTO[];
  onDone: () => void;
}

/**
 * Card flipper shown before a level starts. Each word card auto-speaks the
 * English on appear, lets the student tap to hear it again, and shows the
 * Vietnamese + example sentence. Swipe / tap "Next" to advance, or skip.
 */
export function WordIntro({ words, onDone }: Props) {
  const [i, setI] = useState(0);
  const word = words[i];

  useEffect(() => {
    if (!word) return;
    const t = setTimeout(() => speak(word.english, { audioUrl: word.audioUrl }), 200);
    return () => clearTimeout(t);
  }, [word?.id]);

  if (!word) return null;

  const next = () => {
    if (i + 1 >= words.length) onDone();
    else setI(i + 1);
  };

  return (
    <div className="intro-root">
      <div className="row-between" style={{ marginBottom: 12 }}>
        <span className="muted">
          Word {i + 1} of {words.length}
        </span>
        <button className="ghost" onClick={onDone}>
          Skip ↠
        </button>
      </div>

      <div className="intro-card">
        <div className="intro-art-frame">
          <GameImage className="intro-image" src={word.imageUrl} alt={word.english} fallbackLabel="★" />
        </div>
        <button
          className="intro-speaker"
          onClick={() => speak(word.english, { audioUrl: word.audioUrl })}
          aria-label="Replay audio"
        >
          🔊
        </button>
        <div className="intro-english">{word.english}</div>
        {word.sentence && (
          <button
            className="intro-sentence"
            onClick={() => speak(word.sentence, { audioUrl: word.sentenceAudioUrl ?? null })}
            aria-label="Hear sentence"
          >
            "{word.sentence}"
          </button>
        )}
      </div>

      <div className="intro-dots">
        {words.map((_, idx) => (
          <span key={idx} className={`intro-dot ${idx === i ? "active" : ""}`} />
        ))}
      </div>

      <button onClick={next} style={{ width: "100%", marginTop: 16 }}>
        {i + 1 >= words.length ? "Start running →" : "Next →"}
      </button>
    </div>
  );
}
